import { createContext, useState, useEffect } from 'react'
import jwt_decode from "jwt-decode";
import { useHistory } from 'react-router-dom'
const { REACT_APP_DEV_API_URL, REACT_APP_PROD_API_URL, NODE_ENV } = process.env;

const API_URL =
    NODE_ENV === 'production' ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_DEV_API_URL;

const AuthContext = createContext()

export default AuthContext;

export const AuthProvider = ({ children }) => {

    let [authTokens, setAuthTokens] = useState(() => localStorage.getItem('authTokens') ? JSON.parse(localStorage.getItem('authTokens')) : null)
    let [user, setUser] = useState(() => localStorage.getItem('authTokens') ? jwt_decode(localStorage.getItem('authTokens')) : null)
    let [loading, setLoading] = useState(true)
    let [errorMessage, setErrorMessage] = useState('')
    let [successMessage, setSuccessMessage] = useState('')

    const history = useHistory()

    let loginUser = async (e) => {
        e.preventDefault()
        let response = await fetch(`${API_URL}/auth/token/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 'username': e.target.username.value, 'password': e.target.password.value })
        })
        let data = await response.json()

        if (response.status === 200) {
            setAuthTokens(data)
            localStorage.setItem('authTokens', JSON.stringify(data))
            setUser(jwt_decode(data.access))
            history.push('/')
        } else {
            setErrorMessage('Username or password is incorrect')
        }
    }

    let passwordReset = async (e) => {
        e.preventDefault()
        let response = await fetch(`${API_URL}/auth/password/reset/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 'email': e.target.email.value })
        }, (error) => {
            if (error) {
                setErrorMessage('Something went wrong. Please try again later.')
            }
        })

        if (response.status === 200) {
            setErrorMessage('')
            setSuccessMessage('Password reset instructions have been sent')
        } else if (response.status === 500) {
            setErrorMessage('Something went wrong. Please try again later.')
            setSuccessMessage('')
        } else {
            setErrorMessage('Email address is incorrect')
            setSuccessMessage('')
        }
    }

    let passwordResetConfirm = async (e) => {
        e.preventDefault()
        try {
            let response = await fetch(`${API_URL}/auth/password/reset/confirm/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 'new_password1': e.target.password1.value, 'new_password2': e.target.password2.value, 'uid': e.target.id.value, 'token': e.target.token.value })
            })
            let data = await response.json()

            if (response.status === 200) {
                setErrorMessage('')
                setSuccessMessage('Password has been reset')
            } else {
                setErrorMessage('Unable to change password')
                setSuccessMessage('')
            }
        } catch {
            setErrorMessage('Unable to change password')
            setSuccessMessage('')
        }
    }

    let createUserProf = async (data) => {
        let authUser = data.user.id
        let createProfile = await fetch(`${API_URL}/createUserProfile/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + authTokens?.access
            },
            body: JSON.stringify({ 'authUser': authUser })
        })
    }

    let registerUser = async (e) => {
        e.preventDefault()
        let response = await fetch(`${API_URL}/auth/register/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 'username': e.target.username.value, 'email': e.target.email.value, 'password1': e.target.password.value, 'password2': e.target.password.value, 'first_name': e.target.first_name.value, 'last_name': e.target.last_name.value })
        })
        let data = await response.json()
        createUserProf(data)

        e.preventDefault()
        let autoResponse = await fetch(`${API_URL}/auth/token/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 'username': e.target.username.value, 'password': e.target.password.value })
        })
        let autoData = await autoResponse.json()

        if (autoResponse.status === 200) {
            setAuthTokens(autoData)
            setUser(jwt_decode(autoData.access))
            localStorage.setItem('authTokens', JSON.stringify(autoData))
            history.push('/')
        } else {
            alert('Something went wrong')
        }
    }

    let logoutUser = async () => {
        setLoading(false)
        setAuthTokens(null)
        setUser(null)
        localStorage.removeItem('authTokens')
        history.push('/login')
    }

    let updateToken = async () => {
        if (authTokens) {
            let response = await fetch(`${API_URL}/auth/token/refresh/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 'refresh': authTokens?.refresh })
            })
            let data = await response.json()

            if (response.status === 200) {
                setAuthTokens(data)
                setUser(jwt_decode(data.access))
                localStorage.setItem('authTokens', JSON.stringify(data))
            } else {
                logoutUser()
            }
        }
        if (loading) {
            setLoading(false)
        }
    }

    // Pass variables and data to the page
    let contextData = {
        user: user,
        loginUser: loginUser,
        passwordReset: passwordReset,
        passwordResetConfirm: passwordResetConfirm,
        registerUser: registerUser,
        logoutUser: logoutUser,
        setAuthTokens: setAuthTokens,
        setUser: setUser,
        errorMessage: errorMessage,
        successMessage: successMessage,
        authTokens: authTokens
    }

    useEffect(() => {
        // Update token on load
        if (loading) {
            updateToken()

        } else {
            setLoading(false)
        }

        let interval = setInterval(() => {
            if (authTokens) {
                updateToken()
            }
            // Refresh token every 4 minutes
        }, 240000)
        return () => clearInterval(interval)

    }, [authTokens, loading])

    return (
        <AuthContext.Provider value={contextData} >
            {loading ? null : children}
        </AuthContext.Provider>
    )
}